import React from "react";
import Button from "@mui/material/Button";
import styles from "./uploadLogisticsCsvDialog.module.scss";
import { Dialog, DialogContent, DialogTitle, CircularProgress } from "@mui/material";
import { excelFileReader } from "../../../libs/helpers";
import { useDispatch } from "react-redux";
import { error } from "../../../apis/reducers/snackbarsReducer";
import { useParams } from "react-router-dom";
import { LogisticsCsvUploadErrResponseData, LogisticsCsvUploadResponseData } from "../../../apis/models/LogisticsFeeResponse";
import { postLogisticFeeCSV } from "../../../apis/invoice-api";

const UploadLogisticsCsvDialog: React.FC<UploadLogisticsCsvDialogProps> = (props: UploadLogisticsCsvDialogProps) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { open, onClose, onSuccess: onRefreshData } = props;
  const [fileName, setFileName] = React.useState<string>("");
  const enum Step {
    INITIAL = "initial",
    LOADING = "loading",
    RESULT = "result"
  }
  const [step, setStep] = React.useState<"initial" | "loading" | "result">(Step.INITIAL);
  const [errRows, setErrRows] = React.useState<number[]>([]);
  const [originalCsvData, setOriginalCsvData] = React.useState<string[]>([]);
  const [csvUploadResponse, setCsvUploadResponse] = React.useState<LogisticsCsvUploadResponseData | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) { return }
    const file = e.target.files[0];
    if (!file) { return }
    if (!id || id === undefined) {return}
    const fileStringRows = await excelFileReader(file) as string[][];
    const fileString = fileStringRows.map(item => item.join(","));
    fileString.splice(0,1); // remove header
    setOriginalCsvData(fileString);
    setFileName(file.name);
  }

  const handleDropFile = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      const file = e.dataTransfer.files[0];
      if (!file) { return }
      if (!file.name.includes(".xls")) {
        dispatch(error("Must be Excel file"));
        return
      }
      if (!id || id === undefined) {return}
      const fileStringRows = await excelFileReader(file) as string[][];
      const fileString = fileStringRows.map(item => item.join(","));
      fileString.splice(0,1); // remove header
      setOriginalCsvData(fileString);
      setFileName(file.name);
    }
  }

  function splitFileString(fileString: string) {
    const fileRows = fileString.split("\n");
    fileRows.splice(0,1); // remove header
    const newFileRows = fileRows.map(item => item.replaceAll("\r", ""));
    if (newFileRows.length > 0 && newFileRows[newFileRows.length - 1].length === 0) {
      return newFileRows.slice(0, newFileRows.length - 1)
    }
    return newFileRows
  }

  const uploadCsv = () => {
    setStep(Step.LOADING);
    if (!id || id === undefined) {return}
    postLogisticFeeCSV(id, originalCsvData)
      .then(res => {
        setCsvUploadResponse(res.data.data);
        const errRowNu: number[] = [];
        res.data.data.errors.forEach((item: LogisticsCsvUploadErrResponseData) => {
          errRowNu.push(item.rowNumber);
        })
        if (errRowNu.length > 0) {
          downloadErrorData("日付,品番,カラー/SIZE,数量,検数単価,出荷単価", res.data.data.errors)
        }
        setErrRows(errRowNu);
        setStep(Step.RESULT);
        onRefreshData();
      })
      .catch(err => console.log(err))
  }

  function downloadErrorData(header: string, data: LogisticsCsvUploadErrResponseData[]) {
    let csvContent = `${header},エラーメッセージ\n`;
    data.forEach(errData => {
      csvContent += `${errData.rowData},${errData.message}\n`;
    })

    // append Unicode BOM
    const blob = new Blob([decodeURIComponent('%ef%bb%bf'), csvContent], {type: 'text/csv;charset=utf-8'})

    let encodedUri = URL.createObjectURL(blob);
    let link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "logistic_upload_error_data.csv");
    document.body.appendChild(link);
    link.click();
  }

  const cancel = () => {
    onClose();
    setOriginalCsvData([]);
    setFileName("");
    setStep(Step.INITIAL)
  }


  return (
    <Dialog open={open} onClose={() => cancel()} maxWidth="lg">
      <DialogTitle
        sx={{ fontWeight: 700, fontSize: "24px", padding: "40px 40px 24px" }}
      >
        物流費アップロード
      </DialogTitle>
      <DialogContent sx={{ padding: "0 40px 24px" }}>
        { step === Step.INITIAL ?
          <div>
            <div className={styles.fileUpload}>
              <Button variant="contained" component="label">
                ファイルを選択
                <input hidden accept=".xls, .xlsx" type="file" onChange={(e) => handleFileChange(e)}/>
              </Button>
              <div>{fileName}</div>
            </div>
            <div className={styles.drop_box} onDrop={(e) => handleDropFile(e)} onDragOver={(e) => e.preventDefault()}>
              <img
                src="/icons/fileUploadLogo.svg"
                alt="fileUploadLogo"
              />
              <p>このエリアにドラッグ＆ドロップでファイルを追加</p>
            </div>
            <div className={styles.functions}>
              <Button variant="outlined" onClick={() => cancel()}>キャンセル</Button>
              <Button variant="contained" onClick={() => uploadCsv()} disabled={fileName.length <= 0}>アップロード</Button>
            </div>
          </div> : step === Step.RESULT ?
          <div className={styles.result}>
            <div className={styles.result_header}>
              <div>物流費をアップロードしました。</div>
              <div>下記のデータを更新しました。</div>
            </div>
            <div className={styles.table_wrapper}>
              <table className={styles.result_table}>
                <tbody>
                  <tr>
                    <th>品番</th>
                    <th>色</th>
                  </tr>
                  {
                    csvUploadResponse && csvUploadResponse.successes.map((item, index) => (
                      <tr key={index}>
                        <td>{item.productNumber}</td>
                        <td>{item.colors}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
            {
              errRows.length > 0 ? 
              <div className={styles.result_error}>
                <span>
                  データはエラーにより登録されませんでした。<br/>
                  データを確認してください。<br/>
                </span>
                行No. {errRows.join(", ")}
              </div> :
              <></>
            }
            <div className={styles.result_functions}>
              <Button variant="outlined" onClick={() => cancel()}>閉じる</Button>
            </div>
          </div> :
          <div className={styles.loading}>
            <CircularProgress />
          </div>
        }
      </DialogContent>
    </Dialog>
  );
};

export default UploadLogisticsCsvDialog;

export type UploadLogisticsCsvDialogProps = {
  open: boolean,
  onClose: () => void,
  onSuccess: () => void
}